// src/components/UserSettingsContent.jsx
import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Switch,
  Select,
  MenuItem,
  Button,
  Alert,
  FormControlLabel,
  FormGroup,
  Checkbox,
} from "@mui/material";
import { SettingsContext } from "../context/SettingsContext";
import { AuthContext } from "../context/AuthContext";
import config from "../config.js";

// const DIET_OPTIONS = ["Vegetarian", "Vegan", "Gluten-Free", "Keto", "Paleo"];
const DIET_OPTIONS = config.dietOptions;

const FOOD_ALLERGIES = config.foodAllergies;

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * UserSettingsContent:
 * Renders different sub-panels (Profile, Security, Preferences, Customization)
 * based on the `category` prop.
 */
function UserSettingsContent({ category }) {
  // We'll define a few sub-panels below:

  // 1) Profile Panel (Placeholder)
  function ProfilePanel() {
    const { firstName, lastName, gender, birthday, updateProfile } =
      useContext(SettingsContext);

    // local states to let user edit
    const [draftFirstName, setDraftFirstName] = useState("");
    const [draftLastName, setDraftLastName] = useState("");
    const [draftGender, setDraftGender] = useState("");
    const [birthdayMonth, setBirthdayMonth] = useState("");
    const [birthdayDay, setBirthdayDay] = useState("");
    // const [draftTypewriterEffect, setDraftTypewriterEffect] = useState(false);

    useEffect(() => {
      // initialize local states from context
      setDraftFirstName(firstName || "");
      setDraftLastName(lastName || "");
      setDraftGender(gender || "");

      if (birthday) {
        setBirthdayMonth(birthday.month?.toString() || "");
        setBirthdayDay(birthday.day?.toString() || "");
      } else {
        setBirthdayMonth("");
        setBirthdayDay("");
      }
    }, [firstName, lastName, gender, birthday]);

    // handle month/day logic
    const handleMonthChange = (e) => {
      const m = e.target.value;
      setBirthdayMonth(m);
      if (m) {
        const maxDays = MONTH_DAYS[parseInt(m, 10) - 1];
        if (birthdayDay && parseInt(birthdayDay, 10) > maxDays) {
          setBirthdayDay(maxDays.toString());
        }
      }
    };
    const handleDayChange = (e) => setBirthdayDay(e.target.value);

    const handleSaveProfile = async () => {
      const b =
        birthdayMonth && birthdayDay
          ? {
              month: parseInt(birthdayMonth, 10),
              day: parseInt(birthdayDay, 10),
            }
          : null;

      await updateProfile({
        firstName: draftFirstName,
        lastName: draftLastName,
        gender: draftGender,
        birthday: b,
      });
      // Optionally show success/failure messages
    };

    // build day options
    let maxDays = 31;
    if (birthdayMonth) {
      const monthIndex = parseInt(birthdayMonth, 10) - 1;
      maxDays = MONTH_DAYS[monthIndex];
    }
    const dayOptions = [];
    for (let d = 1; d <= maxDays; d++) dayOptions.push(d);

    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Profile
        </Typography>
        <TextField
          label="First Name"
          value={draftFirstName}
          onChange={(e) => setDraftFirstName(e.target.value)}
          sx={{ mb: 5, maxWidth: 300 }}
          fullWidth={true}
          // sx={{ mb: 2 }}
        />
        <TextField
          label="Last Name"
          value={draftLastName}
          onChange={(e) => setDraftLastName(e.target.value)}
          sx={{ mb: 2, maxWidth: 300 }}
          fullWidth={true}
        />

        <Typography variant="body2" sx={{ mt: 2 }}>
          Gender (Optional):
        </Typography>
        <Select
          value={draftGender}
          onChange={(e) => setDraftGender(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="">Not specified</MenuItem>
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </Select>

        <Typography variant="body2" sx={{ mt: 2 }}>
          Birthday (Optional):
        </Typography>
        {/* Month dropdown */}
        <Select
          value={birthdayMonth}
          onChange={handleMonthChange}
          displayEmpty
          sx={{ width: "100px", mr: 2 }}
        >
          <MenuItem value="">
            <em>Month</em>
          </MenuItem>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <MenuItem key={m} value={m.toString()}>
              {m}
            </MenuItem>
          ))}
        </Select>
        {/* Day dropdown */}
        <Select
          value={birthdayDay}
          onChange={handleDayChange}
          displayEmpty
          sx={{ width: "100px" }}
        >
          <MenuItem value="">
            <em>Day</em>
          </MenuItem>
          {dayOptions.map((d) => (
            <MenuItem key={d} value={d.toString()}>
              {d}
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" onClick={handleSaveProfile}>
            Save Profile
          </Button>
        </Box>
      </Box>
    );
  }

  // 2) Security Panel: "Change Password"
  // local state for password changes

  function SecurityPanel() {
    const { changePassword, deleteAccount } = useContext(AuthContext);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [changePassError, setChangePassError] = useState(null);
    const [changePassSuccess, setChangePassSuccess] = useState(null);

    async function handleChangePasswordSubmit() {
      try {
        setChangePassError(null);
        setChangePassSuccess(null);

        if (newPassword !== confirmNewPassword) {
          setChangePassError("New password and confirmation do not match.");
          return;
        }

        changePassword(currentPassword, newPassword)
          .then(() => {
            setChangePassSuccess("Password changed successfully!");
            alert("Your password has been changed.");
            // Possibly redirect or close the settings overlay
          })
          .catch((err) => {
            setChangePassError(err.message || "Failed to change password.");
            alert("Failed to delete account: " + err.message);
          });

        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
      } catch (err) {
        console.error("Change password error:", err);
        setChangePassError("Failed to change password. Please try again.");
      }
    }

    const handleDeleteClick = () => {
      if (
        window.confirm(
          "Are you sure you want to delete your account? This cannot be undone."
        )
      ) {
        deleteAccount()
          .then(() => {
            alert("Your account has been deleted.");
            // Possibly redirect or close the settings overlay
          })
          .catch((err) => {
            alert("Failed to delete account: " + err.message);
          });
      }
    };

    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Security
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Change Password
        </Typography>

        {changePassSuccess && (
          <Alert
            severity="success"
            onClose={() => setChangePassSuccess(null)}
            sx={{ my: 1 }}
          >
            {changePassSuccess}
          </Alert>
        )}
        {changePassError && (
          <Alert
            severity="error"
            onClose={() => setChangePassError(null)}
            sx={{ my: 1 }}
          >
            {changePassError}
          </Alert>
        )}

<FormGroup>
        <TextField
          label="Current Password"
          // variant="outlined"
          type="password"
          fullWidth
          sx={{ mt: 2, maxWidth: 300 }}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <TextField
          label="New Password"
          // variant="outlined"
          type="password"
          fullWidth
          sx={{ mt: 2, maxWidth: 300 }}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <TextField
          label="Confirm New Password"
          // variant="outlined"
          type="password"
          fullWidth
          sx={{ mt: 2, maxWidth:300 }}
          value={confirmNewPassword}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
        />
        </FormGroup>
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleChangePasswordSubmit}
        >
          Update Password
        </Button>
        <Typography variant="body2" sx={{ mt: 5 }}>
          Delete your account
        </Typography>
        <Button variant="contained" color="error" onClick={handleDeleteClick}>
          Delete Account
        </Button>
      </Box>
    );
  }

  const {
    rememberPreferences,
    darkMode,
    chefPersona,
    language,
    dietaryRestrictions,
    measurementUnits,
    // preferredCuisine,
    notifications,
    foodAllergies,
    typewriterEffect,
    chatTextSize,
    updateSettings,
  } = useContext(SettingsContext);

  // local states to allow user to edit
  const [draftRememberPreferences, setDraftRememberPreferences] = useState(rememberPreferences);
  const [draftDarkMode, setDraftDarkMode] = useState(darkMode);
  const [draftTypewriterEffect, setDraftTypewriterEffect] =
    useState(typewriterEffect);
  const [draftChatTextSize, setDraftChatTextSize] = useState(chatTextSize);
  const [draftChefPersona, setDraftChefPersona] = useState(chefPersona);
  const [draftLanguage, setDraftLanguage] = useState(language);
  const [draftDietaryRestrictions, setDraftDietaryRestrictions] =
    useState(dietaryRestrictions);
  const [draftMeasurementUnits, setDraftMeasurementUnits] =
    useState(measurementUnits);
  // const [draftPreferredCuisine, setDraftPreferredCuisine] =
  //   useState(preferredCuisine);
  const [draftNotifications, setDraftNotifications] = useState(notifications);
  const [draftFoodAllergies, setDraftFoodAllergies] = useState(foodAllergies);

  useEffect(() => {
    setDraftRememberPreferences(rememberPreferences);
    setDraftDarkMode(darkMode);
    setDraftTypewriterEffect(typewriterEffect);
    setDraftChatTextSize(chatTextSize);
    setDraftChefPersona(chefPersona);
    setDraftLanguage(language);
    setDraftDietaryRestrictions(dietaryRestrictions);
    setDraftMeasurementUnits(measurementUnits);
    // setDraftPreferredCuisine(preferredCuisine);
    setDraftNotifications(notifications);
    setDraftFoodAllergies(foodAllergies);
  }, [
    darkMode,
    chefPersona,
    language,
    dietaryRestrictions,
    measurementUnits,
    // preferredCuisine,
    notifications,
    foodAllergies,
    typewriterEffect,
    chatTextSize,
  ]);

  function toggleFoodAllergy(allergy) {
    const hasAllergy = draftFoodAllergies.includes(allergy);
    if (hasAllergy) {
      setDraftFoodAllergies((prev) => prev.filter((a) => a !== allergy));
    } else {
      setDraftFoodAllergies((prev) => [...prev, allergy]);
    }
  }

  function toggleDietaryRestriction(restriction) {
    const hasRestriction = draftDietaryRestrictions.includes(restriction);
    if (hasRestriction) {
      setDraftDietaryRestrictions((prev) =>
        prev.filter((r) => r !== restriction)
      );
    } else {
      setDraftDietaryRestrictions((prev) => [...prev, restriction]);
    }
  }

  function handleSavePreferences() {
    updateSettings({
      rememberPreferences: draftRememberPreferences,
      darkMode: draftDarkMode,
      typewriterEffect: draftTypewriterEffect,
      chatTextSize: draftChatTextSize,
      chefPersona: draftChefPersona,
      language: draftLanguage,
      dietaryRestrictions: draftDietaryRestrictions,
      measurementUnits: draftMeasurementUnits,
      // preferredCuisine: draftPreferredCuisine,
      notifications: draftNotifications,
      foodAllergies: draftFoodAllergies,
    });
  }

  function DisplaySettingsPanel() {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Display
        </Typography>

<FormGroup>


        {/* Dark Mode */}
        <FormControlLabel
          control={
            <Switch
              checked={draftDarkMode}
              onChange={(e) => setDraftDarkMode(e.target.checked)}
            />
          }
          label="Dark Mode"
        />
        <FormControlLabel
          control={
            <Switch
              checked={draftTypewriterEffect}
              onChange={(e) => setDraftTypewriterEffect(e.target.checked)}
            />
          }
          label="Typewriter effect"
        />

</FormGroup>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Chat Text Size:
        </Typography>
        <Select
          value={draftChatTextSize}
          onChange={(e) => setDraftChatTextSize(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="small">Small</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="large">Large</MenuItem>
        </Select>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleSavePreferences}>
            Save Preferences
          </Button>
        </Box>
      </Box>
    );
  }

  function PreferencesPanel() {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Preferences
        </Typography>

        {/* Remember preferences after logout */}
        <FormControlLabel
          control={
            <Switch
              checked={draftRememberPreferences}
              onChange={(e) => setDraftRememberPreferences(e.target.checked)}
            />
          }
          label="Remember my preferences after I sign out"
        />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Instructor Persona:
        </Typography>
        <Select
          value={draftChefPersona}
          onChange={(e) => setDraftChefPersona(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="a friendly cooking instructor">Friendly</MenuItem>
          <MenuItem value="television personality and chef Gordon Ramsey">
            Gordon Ramsey
          </MenuItem>
          <MenuItem value="television personality and chef Julia Child">
            Julia Child
          </MenuItem>
          <MenuItem value="television personality and chef Emeril Lagasse">
            Emeril Lagasse
          </MenuItem>
          <MenuItem value="television personality and chef Martha Stewart">
            Martha Stewart
          </MenuItem>
          <MenuItem value="television personality and chef Guy Fieri">
            Guy Fieri
          </MenuItem>
          <MenuItem value="television personality and chef Rachael Ray">
            Rachael Ray
          </MenuItem>
          <MenuItem value="television personality and chef Jacques Pépin">
            Jacques Pépin
          </MenuItem>
          <MenuItem value="television personality and chef Martin Yan">
            Martin Yan
          </MenuItem>
          <MenuItem value="television personality and chef Bobby Flay">
            Bobby Flay
          </MenuItem>
          <MenuItem value="television personality and chef Jamie Oliver">
            Jamie Oliver
          </MenuItem>
          <MenuItem value="television personality and chef Anthony Bourdain">
            Anthony Bourdain
          </MenuItem>
        </Select>

        {/* Language */}
        <Typography variant="body2" sx={{ mt: 2 }}>
          Language:
        </Typography>
        <Select
          value={draftLanguage}
          onChange={(e) => setDraftLanguage(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="en">English</MenuItem>
          {/* add more if needed */}
        </Select>

        {/* Notifications */}
        <Typography variant="body2" sx={{ mt: 2 }}>
          Notifications:
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!draftNotifications.email}
                onChange={(e) =>
                  setDraftNotifications((prev) => ({
                    ...prev,
                    email: e.target.checked,
                  }))
                }
              />
            }
            label="Email Notifications"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!draftNotifications.push}
                onChange={(e) =>
                  setDraftNotifications((prev) => ({
                    ...prev,
                    push: e.target.checked,
                  }))
                }
              />
            }
            label="Push Notifications"
          />
        </FormGroup>

        {/* System of units */}
        <Typography variant="body2" sx={{ mt: 2 }}>
          Recipe Measurement Units:
        </Typography>
        <Select
          value={draftMeasurementUnits}
          onChange={(e) => setDraftMeasurementUnits(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="international">International (Metric)</MenuItem>
          <MenuItem value="United States">U.S.</MenuItem>
          <MenuItem value="Imperial">Imperial</MenuItem>
        </Select>

        {/* Dietary Restrictions */}
        <Typography variant="body2" sx={{ mt: 2 }}>
          Dietary Restrictions:
        </Typography>
        {/* We wrap checkboxes in a Box or FormGroup that can do row/column layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 1, // small gap between items
            maxWidth: "600px", // optional, to limit how wide it can stretch
          }}
        >
          {DIET_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt}
              control={
                <Checkbox
                  checked={draftDietaryRestrictions.includes(opt)}
                  onChange={() => toggleDietaryRestriction(opt)}
                />
              }
              label={opt}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            />
          ))}
        </Box>

        {/* <Typography variant="body2" sx={{ mt: 2 }}>
          Dietary Restrictions:
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={draftDietaryRestrictions.includes("vegetarian")}
                onChange={() => toggleDietaryRestriction("vegetarian")}
              />
            }
            label="Vegetarian"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draftDietaryRestrictions.includes("vegan")}
                onChange={() => toggleDietaryRestriction("vegan")}
              />
            }
            label="Vegan"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draftDietaryRestrictions.includes("gluten-free")}
                onChange={() => toggleDietaryRestriction("gluten-free")}
              />
            }
            label="Gluten-Free"
          />
        </FormGroup> */}

        {/* Preferred Cuisine */}
        {/* <Typography variant="body2" sx={{ mt: 2 }}>
          Preferred Cuisine:
        </Typography>
        <TextField
          value={draftPreferredCuisine}
          onChange={(e) => setDraftPreferredCuisine(e.target.value)}
          placeholder="e.g. Italian"
          sx={{ mb: 2 }}
          fullWidth
        /> */}

                {/* Food Allergies: The Big Nine */}
        <Typography variant="body2" sx={{ mt: 2 }}>
          Food Allergies (Big Nine):
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 1,
            maxWidth: "600px",
          }}
        >
          {FOOD_ALLERGIES.map((allergy) => (
            <FormControlLabel
              key={allergy}
              control={
                <Checkbox
                  checked={draftFoodAllergies.includes(allergy)}
                  onChange={() => toggleFoodAllergy(allergy)}
                />
              }
              label={allergy}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            />
          ))}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleSavePreferences}>
            Save Preferences
          </Button>
        </Box>
      </Box>
    );
  }

  // 4) Customization Panel (placeholder)
  function CustomizationPanel() {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Customization
        </Typography>
        <Typography variant="body2">
          (Placeholder) If you have advanced theming or UI customizations,
          they'd go here.
        </Typography>
      </Box>
    );
  }

  // Decide which panel to render based on `category`
  switch (category) {
    case "profile":
      return <ProfilePanel />;
    case "security":
      return <SecurityPanel />;
    case "display":
      return <DisplaySettingsPanel />;
    case "preferences":
      return <PreferencesPanel />;
    case "customization":
      return <CustomizationPanel />;
    default:
      // fallback
      return (
        <Box>
          <Typography variant="h6">Select a category</Typography>
        </Box>
      );
  }
}

export default UserSettingsContent;
